import React, { useState, useEffect } from 'react';

const CameraConfig = ({ cameras, setCameras, onAdd, onUpdate, onRemove }) => {
    const [testingCamera, setTestingCamera] = useState(null);
    const [connectedCameras, setConnectedCameras] = useState(new Set());

    // Maximum number of cameras allowed
    const MAX_CAMERAS = 5;

    const testCamera = async (camera) => {
        setTestingCamera(camera.id);
        try {
            // console.log(`Testing camera: ${camera.name} at ${camera.mjpegUrl}`);
            
            if (window.electronAPI && window.electronAPI.invoke) {
                const result = await window.electronAPI.invoke('test-camera-connection', {
                    url: camera.mjpegUrl,
                    username: camera.username || '',
                    password: camera.password || '',
                    authType: camera.authType || 'none',
                    cameraId: camera.id
                });
                
                if (result.success) {
                    alert(`✅ Camera test successful!\nCamera: ${camera.name}\nConnection verified with ${camera.authType || 'no'} authentication.`);
                } else {
                    throw new Error(result.error || 'Connection failed');
                }
            }
        } catch (error) {
            console.error('Camera test failed:', error);
            alert(`❌ Camera test failed!\nError: ${error.message}\n\nPlease check:\n• URL is correct\n• Authentication credentials\n• Network connectivity`);
        } finally {
            setTestingCamera(null);
        }
    };

    const setPrimaryCamera = (cameraId) => {
        // Only one camera can be primary
        setCameras(cameras.map(cam => ({
            ...cam,
            isPrimary: cam.id === cameraId
        })));
    };

	const connectCamera = async (camera, index) => {
        // console.log(`Connecting to camera ${index}: ${camera.name} at ${camera.mjpegUrl}`);
        
        try {
            // Use the new camera handler API
            if (window.electronAPI && window.electronAPI.invoke) {
                const result = await window.electronAPI.invoke('start-camera', {
                    url: camera.mjpegUrl,
                    username: camera.username || '',
                    password: camera.password || '',
                    cameraId: camera.id,  // Use camera.id, not index
                    authType: camera.authType || 'none'
                });
                
                if (result.success) {
                    // Set up frame handler for this camera using camera.id
                    window.electronAPI.on(`camera-frame-${camera.id}`, (jpegBase64) => {
                        const cameraElements = document.querySelectorAll(`[data-camera-id="${camera.id}"]`);
                        cameraElements.forEach(el => {
                            el.src = `data:image/jpeg;base64,${jpegBase64}`;
                        });
                    });
                    
                    // Mark camera as connected
                    setConnectedCameras(prev => new Set([...prev, camera.id]));
                    // console.log(`✅ Camera ${camera.name} connected successfully`);
                } else {
                    throw new Error(result.error || 'Failed to start camera');
                }
            }
        } catch (error) {
            console.error(`❌ Failed to connect camera ${camera.name}:`, error);
            alert(`Failed to connect camera ${camera.name}: ${error.message}`);
        }
    };

	const disconnectCamera = async (cameraId) => {
        // console.log(`Disconnecting camera ${cameraId}`);
        
        try {
            if (window.electronAPI && window.electronAPI.invoke) {
                await window.electronAPI.invoke('stop-camera', cameraId);
            }
            
            // Remove frame listener
            window.electronAPI.removeAllListeners(`camera-frame-${cameraId}`);
            
            // Clear the preview image
            const cameraElements = document.querySelectorAll(`[data-camera-id="${cameraId}"]`);
            cameraElements.forEach(el => {
                el.src = '';
            });
            
            // Mark camera as disconnected
            setConnectedCameras(prev => {
                const newSet = new Set(prev);
                newSet.delete(cameraId);
                return newSet;
            });
            
            // console.log(`✅ Camera ${cameraId} disconnected successfully`);
        } catch (error) {
            console.error(`❌ Failed to disconnect camera ${cameraId}:`, error);
        }
    };

    const handleAddCamera = () => {
        if (cameras.length >= MAX_CAMERAS) {
            alert(`Maximum of ${MAX_CAMERAS} cameras allowed. Please remove an existing camera before adding a new one.`);
            return;
        }
        onAdd();
    };

	// Cleanup function to stop camera streams when component unmounts
	useEffect(() => {
		return () => {
			if (window.camera && window.camera.stopAll) {
				window.camera.stopAll();
			}
			// Also stop all camera streams via main process
			if (window.electronAPI && window.electronAPI.invoke) {
				window.electronAPI.invoke('stop-all-cameras');
			}
		};
	}, []);

    return (
        <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3><i className="fas fa-video"></i> Camera Settings</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ 
                        fontSize: '0.875rem', 
                        color: cameras.length >= MAX_CAMERAS ? '#dc3545' : '#6c757d' 
                    }}>
                        {cameras.length}/{MAX_CAMERAS} cameras
                    </span>
                    <button 
                        className="btn btn-primary" 
                        onClick={handleAddCamera}
                        disabled={cameras.length >= MAX_CAMERAS}
                        style={{ 
                            opacity: cameras.length >= MAX_CAMERAS ? 0.6 : 1,
                            cursor: cameras.length >= MAX_CAMERAS ? 'not-allowed' : 'pointer'
                        }}
                    >
                        ➕ Add Camera
                    </button>
                </div>
            </div>

            {cameras.length >= MAX_CAMERAS && (
                <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
                    <p><strong>Camera Limit Reached:</strong> You have configured the maximum of {MAX_CAMERAS} cameras. Remove an existing camera to add a new one.</p>
                </div>
            )}

            {cameras.map((camera, index) => (
                <div key={camera.id} className="card" style={{ margin: '1rem 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4>Camera {index + 1}</h4>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {camera.mjpegUrl && (
                                connectedCameras.has(camera.id) ? (
                                    <button 
                                        className="btn btn-warning btn-small" 
                                        onClick={() => disconnectCamera(camera.id)}
                                    >
                                        <i className="fas fa-stop"></i> Disconnect
                                    </button>
                                ) : (
                                    <button 
                                        className="btn btn-success btn-small" 
                                        onClick={() => connectCamera(camera, index)}
                                    >
                                        <i className="fas fa-play"></i> Connect
                                    </button>
                                )
                            )}
                            <button 
                                className="btn btn-secondary btn-small" 
                                onClick={() => onRemove(camera.id)}
                            >
                                <i className="fas fa-trash"></i> Remove
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                        {/* Left Half: Form Inputs */}
                        <div style={{ flex: '1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div className="form-group">
                                <label className="form-label">Camera Name</label>
                                <input
                                    className="form-input"
                                    value={camera.name}
                                    onChange={(e) => onUpdate(camera.id, 'name', e.target.value)}
                                    placeholder="License Plate Camera"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">MJPEG Stream URL</label>
                                <input
                                    className="form-input"
                                    value={camera.mjpegUrl}
                                    onChange={(e) => onUpdate(camera.id, 'mjpegUrl', e.target.value)}
                                    placeholder="http://192.168.1.100:8080/video"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Authentication Type</label>
                                <select
                                    className="form-select"
                                    value={camera.authType}
                                    onChange={(e) => onUpdate(camera.id, 'authType', e.target.value)}
                                >
                                    <option value="none">None</option>
                                    <option value="basic">Basic Auth</option>
                                    {/* <option value="digest">Digest Auth</option> */}
                                </select>
                            </div>

                            {camera.authType !== 'none' && camera.authType !== 'token' ? (
                                <div className="form-group">
                                    <label className="form-label">Username</label>
                                    <input
                                        className="form-input"
                                        value={camera.username}
                                        onChange={(e) => onUpdate(camera.id, 'username', e.target.value)}
                                        autoComplete="username"
                                    />
                                </div>
                            ) : (
                                <div className="form-group">
                                    {/* Empty div to maintain grid layout */}
                                </div>
                            )}

                            {camera.authType !== 'none' && camera.authType !== 'token' && (
                                <div className="form-group">
                                    <label className="form-label">Password</label>
                                    <input
                                        className="form-input"
                                        type="password"
                                        value={camera.password}
                                        onChange={(e) => onUpdate(camera.id, 'password', e.target.value)}
                                        autoComplete="current-password"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Right Half: Camera Preview */}
                        {camera.mjpegUrl && (
                            <div style={{ flex: '1', marginTop: '0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <label className="form-label">Camera Preview</label>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.5rem',
                                        fontSize: '0.875rem'
                                    }}>
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: connectedCameras.has(camera.id) ? '#48bb78' : '#e2e8f0'
                                        }}></div>
                                        <span style={{ color: connectedCameras.has(camera.id) ? '#48bb78' : '#718096' }}>
                                            {connectedCameras.has(camera.id) ? 'Connected' : 'Disconnected'}
                                        </span>
                                    </div>
                                </div>
                                <div className="camera-feed" style={{ 
                                    minHeight: '200px', 
                                    background: '#f5f5f5', 
                                    position: 'relative',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                }}>
                                    <img 
                                        data-camera-id={camera.id}
                                        data-src={camera.mjpegUrl} 
                                        className="camera" 
                                        alt="Camera Preview" 
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover'
                                        }} 
                                    />
                                    <div className="camera-overlay" style={{
                                        position: 'absolute',
                                        bottom: '0',
                                        left: '0',
                                        right: '0',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        padding: '0.5rem',
                                        fontSize: '0.875rem'
                                    }}>
                                        {camera.name || `Camera ${index + 1}`}
                                        {camera.isPrimary && ' (Primary)'}
                                        {camera.authType && camera.authType !== 'none' && ` - ${camera.authType.toUpperCase()} Auth`}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={camera.isPrimary}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setPrimaryCamera(camera.id);
                                    }
                                }}
                            />
                            <span>Primary License Plate Camera</span>
                        </label>
                        <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                            The primary camera will be used for automatic license plate detection
                        </p>
                    </div>
                </div>
            ))}

            {cameras.length > 0 && (
                <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#718096' }}>
                    <p><strong>Tips:</strong></p>
                    <ul style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>
                        <li>Ensure cameras have a clear view of license plates</li>
                        <li>Use the primary camera for the best license plate detection angle</li>
                        <li>Click "Connect" to start each camera stream when ready to test</li>
                        <li>Use "Disconnect" to stop camera streams and conserve resources</li>
                        <li>Higher resolution provides better OCR accuracy but uses more bandwidth</li>
                        <li>Maximum of {MAX_CAMERAS} cameras can be configured for optimal performance</li>
                        <li><strong>Authentication:</strong> Use Basic Auth for simple username/password, Digest Auth for enhanced security</li>
                        <li><strong>Basic Auth:</strong> Credentials sent with each request (less secure but widely supported)</li>
                        <li><strong>Digest Auth:</strong> Uses challenge-response mechanism (more secure, recommended for sensitive networks)</li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default CameraConfig;